import React, { useState } from "react";
import { LegalLayout, H2, P } from "../components/LegalLayout";
import Seo from "../components/Seo";
import { Mail, Send, Loader2, Check, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !email.trim() || !message.trim()) {
            toast.error("Please fill in all fields.");
            return;
        }
        setSending(true);
        // Open user's mail client with prefilled message (no backend mail server needed)
        const subject = encodeURIComponent(`Contact from ${name} via Free AI Writers`);
        const body = encodeURIComponent(`Name: ${name}\nEmail: ${email}\n\n${message}`);
        const mailto = `mailto:freeaiwriters@gmail.com?subject=${subject}&body=${body}`;
        // Brief delay to make UI feel intentional
        setTimeout(() => {
            window.location.href = mailto;
            setSending(false);
            setSent(true);
            toast.success("Opening your email app…");
        }, 500);
    };

    return (
        <>
            <Seo
                path="/contact"
                title="Contact Us — Free AI Writers"
                description="Get in touch with Free AI Writers. Send us feedback, feature requests, bug reports, or business inquiries. Email: freeaiwriters@gmail.com"
            />
            <LegalLayout kicker="Say hi" title="Contact Us" lastUpdated={null}>
            <P>
                We'd love to hear from you. Whether you have a feature request,
                a bug to report, want to suggest a new AI tool, or have a
                business inquiry — drop us a message and we'll get back within
                24-48 hours.
            </P>

            <H2>Direct email</H2>
            <a
                href="mailto:freeaiwriters@gmail.com"
                data-testid="contact-email-link"
                className="inline-flex items-center gap-3 nb-border rounded-2xl bg-[#A7F3D0] px-5 py-4 nb-shadow-sm hover:-translate-y-1 hover:shadow-[6px_6px_0px_#0A0A0A] transition-all font-bold no-underline text-black"
            >
                <Mail strokeWidth={3} className="w-5 h-5" />
                freeaiwriters@gmail.com
            </a>

            <H2>Or send us a message</H2>
            <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="space-y-4 not-prose"
            >
                <div>
                    <label className="block text-xs font-bold uppercase tracking-widest mb-1.5">
                        Your name
                    </label>
                    <input
                        data-testid="contact-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Jane Doe"
                        className="w-full bg-neutral-50 nb-border rounded-xl px-4 py-3 font-medium focus:outline-none focus:bg-white focus:nb-shadow-sm"
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold uppercase tracking-widest mb-1.5">
                        Your email
                    </label>
                    <input
                        type="email"
                        data-testid="contact-email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="jane@example.com"
                        className="w-full bg-neutral-50 nb-border rounded-xl px-4 py-3 font-medium focus:outline-none focus:bg-white focus:nb-shadow-sm"
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold uppercase tracking-widest mb-1.5">
                        Message
                    </label>
                    <textarea
                        data-testid="contact-message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={6}
                        placeholder="What's on your mind?"
                        className="w-full bg-neutral-50 nb-border rounded-xl px-4 py-3 font-medium focus:outline-none focus:bg-white focus:nb-shadow-sm resize-none"
                    />
                </div>
                <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit"
                    className="inline-flex items-center gap-2 bg-[#FDE047] text-black nb-border rounded-xl px-6 py-3.5 font-black uppercase tracking-widest text-sm nb-shadow hover:-translate-y-1 hover:shadow-[6px_6px_0px_#0A0A0A] active:translate-y-1 active:translate-x-1 active:shadow-none transition-all disabled:opacity-50"
                >
                    {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} />
                    ) : sent ? (
                        <Check className="w-4 h-4" strokeWidth={3} />
                    ) : (
                        <Send className="w-4 h-4" strokeWidth={3} />
                    )}
                    {sent ? "Email Opened" : sending ? "Opening…" : "Send Message"}
                </button>
            </form>

            <H2>Other ways to reach us</H2>
            <P>
                <MessageSquare className="inline w-4 h-4 mr-1" /> For
                feedback, feature requests, or partnership inquiries — same
                email works.
            </P>
            <P>
                <b>Response time:</b> We aim to reply within 24-48 hours on
                weekdays. For urgent issues with the AI tools, please include
                "URGENT" in your subject line.
            </P>
        </LegalLayout>
        </>
    );
}
